import i18next from 'i18next';
import HttpApi from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import * as Sentry from '@sentry/browser';
import { debounce } from 'lodash-es';

import { moment } from '@hotjar/insights-common/lib/moment';
import { abbrNumber, localeNumber } from '@hotjar/insights-common/lib/number';
import { formatLastActivity } from './utils/formatLastActivity';
import {
  supportedLocales as supportedLocalesConfig,
  extendedSupportedLocales as extendedSupportedLocalesConfig,
} from './utils/supportedLocales';
import EXTERNAL_NAMESPACES from '../namespaces.json';
import hotjarCacheLng from './hotjar-lng-detector';

const HJ_WEBSITE_COOKIE_LOCALE = 'NEXT_LOCALE';

export const USER_LOCALE_KEY = 'user_locale';

// we get localization-dev-mode flag directly from localStorage
const extendedLocalesFlag = window.localStorage.getItem(
  'HJ_OVERRIDE_FEATURE:localization-dev-mode',
);

const supportedLocales =
  extendedLocalesFlag === '1' || extendedLocalesFlag === 'true'
    ? extendedSupportedLocalesConfig
    : supportedLocalesConfig;

const FALLBACK_DEFAULT_LOCALE = supportedLocales[0];

// Missing keys handling
let missingKeys: Array<string> = [];
const DEBOUNCE_TIME = 1000;
const debouncedSentryMessage = debounce(() => {
  Sentry.captureMessage('Missing translation keys', {
    extra: {
      keys: missingKeys,
    },
    level: Sentry.Severity.Warning,
  });
  missingKeys = [];
}, DEBOUNCE_TIME);

const customLanguageDetector = new LanguageDetector();
customLanguageDetector.addDetector(hotjarCacheLng);

const initI18Next = (): void => {
  i18next
    .use(HttpApi)
    .use(initReactI18next)
    .use(customLanguageDetector)
    .init({
      ns: EXTERNAL_NAMESPACES,
      appendNamespaceToCIMode: true,
      fallbackLng: {
        'pt-STAGING': ['pt-BR', 'en'],
        'de-STAGING': ['de', 'en'],
        default: [FALLBACK_DEFAULT_LOCALE],
      },
      supportedLngs: supportedLocales as unknown as string[],
      defaultNS: 'common',
      preload: [supportedLocales[0]],
      backend: {
        loadPath: (lngs: string[], namespaces: string[]) => {
          const ns = (namespaces[0] || '').toLowerCase();
          const lng = (lngs[0] || '').toLowerCase();
          const externalSourcePath = 'https://translations.hotjar.com/translations';

          return `${externalSourcePath}/${ns}/${lng}.i18n.json`;
        },
        crossDomain: true,
      },
      detection: {
        order: ['hotjar'],
        lookupCookie: HJ_WEBSITE_COOKIE_LOCALE,
        lookupLocalStorage: USER_LOCALE_KEY,
        caches: ['localStorage', 'cookie'],
        cookieDomain: '.hotjar.com',
        cookieOptions: { sameSite: true, path: '/' },
        excludeCacheFor: [],
      },
      returnEmptyString: false,
      returnNull: false,
      saveMissing: true,
      missingKeyHandler(...args) {
        // making sure we only send unique key names
        const [, ns, key] = args;

        // don't send missing keys to Sentry until i18next init
        if (i18next.isInitialized) {
          const fullKey = `${ns}:${key}`;

          missingKeys = missingKeys.filter((k) => k !== fullKey).concat([fullKey]);
          debouncedSentryMessage();
        }
      },
      parseMissingKeyHandler: (key: string) => {
        if (process.env.NODE_ENV === 'production') {
          return '';
        }

        return `missing translation for ${key}`;
      },
      interpolation: {
        format: (value: number, format?: string) => {
          if (format === 'formatLastActivity') return formatLastActivity(value, i18next);
          if (format === 'localeNumber') return localeNumber(value) ?? '';
          if (format === 'abbrNumber') return abbrNumber(value) ?? '';

          return String(value);
        },
        escapeValue: false, // not needed for react as it escapes by default
      },
      react: {
        bindI18n: 'languageChanged',
        transSupportBasicHtmlNodes: false,
        transKeepBasicHtmlNodesFor: ['br', 'strong', 'i'],
        useSuspense: false,
      },
    });

  i18next.on('languageChanged', () => {
    // to make sure we use the actual language in use and not the detected one
    moment.locale([i18next.languages[0], supportedLocales[0]]);
  });
};

export { initI18Next, i18next };
